import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

// ── SALT stints helpers ───────────────────────────────────────

const THIS_YEAR = new Date().getFullYear();
const CURRENT_YEAR = String(THIS_YEAR);
const YEAR_OPTIONS = Array.from({ length: THIS_YEAR - 1999 }, (_, i) => 2000 + i).reverse();

function formatStints(stints) {
  if (!stints || stints.length === 0) return null;
  return stints
    .map(s => `${s.from}–${s.to === 'present' ? 'Present' : s.to}`)
    .join(', ');
}

function calcTotalYears(stints) {
  if (!stints || stints.length === 0) return 0;
  return stints.reduce((total, s) => {
    const to = s.to === 'present' ? THIS_YEAR : parseInt(s.to);
    return total + Math.max(0, to - parseInt(s.from) + 1);
  }, 0);
}

function StintsEditor({ stints, onChange }) {
  const add = () => onChange([...stints, { from: THIS_YEAR, to: 'present' }]);
  const remove = (i) => onChange(stints.filter((_, idx) => idx !== i));
  const update = (i, key, val) => {
    const next = stints.map((s, idx) => idx === i ? { ...s, [key]: val } : s);
    onChange(next);
  };

  return (
    <div>
      {stints.map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6B7F5A', whiteSpace: 'nowrap' }}>From</span>
            <select value={s.from} onChange={e => update(i, 'from', parseInt(e.target.value))}
              style={{ padding: '6px 8px', border: '1.5px solid #C8D9B0', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', color: '#2D2D2D' }}>
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6B7F5A', whiteSpace: 'nowrap' }}>To</span>
            <select value={s.to} onChange={e => update(i, 'to', e.target.value === 'present' ? 'present' : parseInt(e.target.value))}
              style={{ padding: '6px 8px', border: '1.5px solid #C8D9B0', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', color: '#2D2D2D' }}>
              <option value="present">Present</option>
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {stints.length > 1 && (
            <button onClick={() => remove(i)} style={{ background: 'none', border: '1.5px solid #f7c1c1', borderRadius: 6, color: '#a32d2d', fontWeight: 700, fontSize: '0.8rem', padding: '4px 10px', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>✕</button>
          )}
        </div>
      ))}
      <button onClick={add} style={{ marginTop: 4, background: 'none', border: '1.5px dashed #C8D9B0', borderRadius: 8, color: '#5A7A3A', fontWeight: 700, fontSize: '0.82rem', padding: '6px 14px', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
        + Add period
      </button>
    </div>
  );
}

function formatPhone(val) {
  if (!val) return val;
  const d = String(val).replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)})${d.slice(3)}`;
  return `(${d.slice(0,3)})${d.slice(3,6)}-${d.slice(6)}`;
}

const STATUS_COLORS = {
  complete:    { bg: '#EAF4E2', color: '#2D5016', label: '✓ Complete' },
  in_progress: { bg: '#FEF6E4', color: '#7a4e00', label: '⏳ In Progress' },
  not_started: { bg: '#F4F4F4', color: '#888',    label: '— Not Started' },
};

function getStatus(row) {
  if (!row) return 'not_started';
  const p = row.personal_info || {};
  const f = row.forms || {};
  const a = row.availability || {};
  const c = row.checks || {};
  const personalComplete = !!(p.firstName && p.lastName && p.phone && p.email && p.address && p.city && p.state && p.zip && p.emergencyName && p.emergencyPhone && p.emergencyRelationship && Object.values(p.shirts || {}).reduce((x,y) => x+y, 0) > 0);
  const formsComplete = !!(f.w4 && f.i9 && f.medical && f.cpr);
  const availComplete = a.ropes !== null && a.ropes !== undefined && a.fancy !== null && a.fancy !== undefined && a.junior !== null && a.junior !== undefined;
  const submitComplete = !!(c.accuracy && c.policies && c.texts && c.liability);
  if (personalComplete && formsComplete && availComplete && submitComplete) return 'complete';
  if (p.firstName || f.w4 || a.ropes !== undefined) return 'in_progress';
  return 'not_started';
}

// ── Shared small components ───────────────────────────────────

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6B7F5A', minWidth: 130 }}>{label}</span>
      <span style={{ fontSize: '0.88rem', color: '#2D2D2D' }}>{value || '—'}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#6B7F5A', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, type = 'text', placeholder }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', color: '#2D2D2D', outline: 'none', boxSizing: 'border-box' }}
    />
  );
}

function FormCheck({ label, done }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
      <span style={{ fontSize: '0.9rem' }}>{done ? '✅' : '⬜'}</span>
      <span style={{ fontSize: '0.85rem', color: done ? '#2D5016' : '#9B9B9B', fontWeight: done ? 600 : 400 }}>{label}</span>
    </div>
  );
}

// ── User nav dropdown ─────────────────────────────────────────

function UserMenu({ email, photoUrl, onMyProfile, onGoToForm, onSignOut, isOnProfile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const item = (icon, label, onClick, danger) => (
    <button
      onClick={() => { setOpen(false); onClick(); }}
      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 16px', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: danger ? '#c0392b' : '#2D2D2D', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}
      onMouseEnter={e => e.currentTarget.style.background = '#F7FAF2'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <span style={{ fontSize: '1rem', width: 20, textAlign: 'center' }}>{icon}</span>
      {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: photoUrl ? '3px 8px 3px 3px' : '6px 12px', fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
      >
        {photoUrl
          ? <img src={photoUrl} alt="Profile" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.6)' }} />
          : <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
        }
        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>▾</span>
      </button>

      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: '#fff', borderRadius: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.14)', minWidth: 210, zIndex: 2000, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px 8px', fontSize: '0.75rem', color: '#9B9B9B', borderBottom: '1px solid #F0F7E8', fontWeight: 600 }}>{email}</div>
          {!isOnProfile && item('👤', 'My Profile', onMyProfile || (() => {}))}
          {item('📋', `Summer ${CURRENT_YEAR}`, onGoToForm || (() => {}))}
          <div style={{ borderTop: '1px solid #F0F7E8', marginTop: 4 }}>
            {item('🚪', 'Sign out', onSignOut, true)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────

function SectionCard({ title, children, onEdit, editing, onSave, onCancel, saving }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #D4E8B0', borderRadius: 12, padding: '18px 20px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#2D5016', fontFamily: "'Nunito', sans-serif" }}>{title}</h3>
        {!editing && onEdit && (
          <button onClick={onEdit} style={s.editBtn}>Edit</button>
        )}
        {editing && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={s.cancelBtn}>Cancel</button>
            <button onClick={onSave} disabled={saving} style={s.saveBtn}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function ProfilePage({ session, onSignOut, onGoToForm, onBack, adminSubmission, isAdminView }) {
  const [submission, setSubmission]   = useState(adminSubmission || null);
  const [loading, setLoading]         = useState(!adminSubmission);
  const [editSection, setEditSection] = useState(null);
  const [draft, setDraft]             = useState({});
  const [saving, setSaving]           = useState(false);
  const [seasonOpen, setSeasonOpen]   = useState(false);

  // Directory profile state
  const [dirProfile, setDirProfile]         = useState({});
  const [dpEditing, setDpEditing]           = useState(false);
  const [dpDraft, setDpDraft]               = useState({});
  const [dpSaving, setDpSaving]             = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [isPublished, setIsPublished]       = useState(false);

  // Photo crop modal state
  const CROP_FRAME  = 300;
  const [cropSrc,      setCropSrc]      = useState(null);
  const [cropOffset,   setCropOffset]   = useState({ x: 0, y: 0 });
  const [cropScale,    setCropScale]    = useState(1);
  const [cropMinScale, setCropMinScale] = useState(1);
  const [cropNatural,  setCropNatural]  = useState({ w: 0, h: 0 });
  const cropImgRef   = useRef(null);
  const photoInputRef = useRef(null);
  const [publishSaving, setPublishSaving]   = useState(false);

  const fetchSubmission = useCallback(async () => {
    if (adminSubmission) { setSubmission(adminSubmission); return; }
    setLoading(true);
    const { data } = await supabase
      .from('onboarding_submissions')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle();
    setSubmission(data);
    setLoading(false);
  }, [session, adminSubmission]);

  useEffect(() => { fetchSubmission(); }, [fetchSubmission]);
  useEffect(() => { setDirProfile(submission?.directory_profile || {}); }, [submission]);

  // Check publish status for admin view
  useEffect(() => {
    if (!isAdminView || !submission?.email) return;
    supabase.from('staff_directory').select('id').eq('email', submission.email).maybeSingle()
      .then(({ data }) => setIsPublished(!!data));
  }, [isAdminView, submission]);

  // Auto-expand season card if onboarding is not complete
  useEffect(() => {
    if (submission) {
      setSeasonOpen(getStatus(submission) !== 'complete');
    }
  }, [submission]);

  const p = submission?.personal_info || {};
  const f = submission?.forms || {};
  const a = submission?.availability || {};
  const status = getStatus(submission);
  const sc = STATUS_COLORS[status];

  const displayEmail = isAdminView ? submission?.email : session?.user?.email;
  const displayName  = p.firstName ? `${p.firstName} ${p.lastName || ''}`.trim()
                     : isAdminView ? (submission?.email || 'Staff Member')
                     : (session?.user?.email || 'Your Profile');
  const initials = displayName.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

  const startEdit  = (section, fields) => { setEditSection(section); setDraft({ ...fields }); };
  const cancelEdit = () => { setEditSection(null); setDraft({}); };
  const set        = field => val => setDraft(prev => ({ ...prev, [field]: val }));
  const setDp      = field => val => setDpDraft(prev => ({ ...prev, [field]: val }));

  const saveEdit = async (section, newFields) => {
    setSaving(true);
    const updated = { ...(submission?.personal_info || {}), ...newFields };
    const { error } = await supabase
      .from('onboarding_submissions')
      .update({ personal_info: updated })
      .eq('id', submission.id);
    if (!error) {
      setSubmission(prev => ({ ...prev, personal_info: updated }));
      setEditSection(null);
      setDraft({});
    }
    setSaving(false);
  };

  const saveDirProfile = async (updates) => {
    setDpSaving(true);
    const updated = { ...dirProfile, ...updates };
    const { error } = await supabase
      .from('onboarding_submissions')
      .update({ directory_profile: updated })
      .eq('id', submission.id);
    if (!error) {
      setSubmission(prev => ({ ...prev, directory_profile: updated }));
      setDirProfile(updated);
      setDpEditing(false);
      setDpDraft({});
    }
    setDpSaving(false);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = CROP_FRAME / Math.min(img.naturalWidth, img.naturalHeight);
        const scaledW = img.naturalWidth  * scale;
        const scaledH = img.naturalHeight * scale;
        setCropNatural({ w: img.naturalWidth, h: img.naturalHeight });
        setCropMinScale(scale);
        setCropScale(scale);
        setCropOffset({ x: (CROP_FRAME - scaledW) / 2, y: (CROP_FRAME - scaledH) / 2 });
        setCropSrc(e.target.result);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const confirmCrop = () => {
    const img = cropImgRef.current;
    if (!img) return;
    const OUT = 600;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    // Fill white so any uncovered pixels aren't black in the JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUT, OUT);
    // Crop to square only — circular display is handled by CSS border-radius
    const sx = -cropOffset.x / cropScale;
    const sy = -cropOffset.y / cropScale;
    const sw = CROP_FRAME   / cropScale;
    const sh = CROP_FRAME   / cropScale;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT, OUT);
    canvas.toBlob(blob => {
      uploadPhoto(new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
      setCropSrc(null);
    }, 'image/jpeg', 0.92);
  };

  const uploadPhoto = async (file) => {
    if (!file || !submission) return;
    setPhotoUploading(true);
    // Unique path per upload so the URL always changes and browsers don't serve a cached old photo
    const path = `${submission.id}_${Date.now()}.jpg`;
    const { error } = await supabase.storage.from('staff-photos').upload(path, file);
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('staff-photos').getPublicUrl(path);
      const updated = { ...dirProfile, photoUrl: publicUrl };
      await supabase.from('onboarding_submissions').update({ directory_profile: updated }).eq('id', submission.id);
      setDirProfile(updated);
      setDpDraft(prev => ({ ...prev, photoUrl: publicUrl }));
      setSubmission(prev => ({ ...prev, directory_profile: updated }));
    }
    setPhotoUploading(false);
  };

  const submitForReview  = () => saveDirProfile({ ...dirProfile, submittedForDirectory: true });

  // Map admin-side roles to public directory categories
  const toDirectoryCategory = (role) => {
    if (role === 'Male Counselor' || role === 'Female Counselor') return 'Counselor';
    return role || 'Other';
  };

  const publishToDirectory = async () => {
    setPublishSaving(true);
    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || submission.email;
    const { error } = await supabase.from('staff_directory').upsert({
      email: submission.email,
      name,
      role:       dirProfile.positionTitle || '',
      category:   toDirectoryCategory(submission.salt_role),
      bio:        dirProfile.bio           || '',
      hometown:   dirProfile.hometown      || '',
      salt_years: dirProfile.saltStints?.length
        ? calcTotalYears(dirProfile.saltStints)
        : dirProfile.saltYears ? parseInt(dirProfile.saltYears) : null,
      photo_url:  dirProfile.photoUrl      || null,
      visible:    true,
    }, { onConflict: 'email' });
    if (error) alert('Publish failed: ' + error.message);
    else setIsPublished(true);
    setPublishSaving(false);
  };

  const removeFromDirectory = async () => {
    if (!window.confirm('Remove this person from the public directory?')) return;
    setPublishSaving(true);
    const { error } = await supabase.from('staff_directory').delete().eq('email', submission.email);
    if (error) alert('Could not remove: ' + error.message);
    else setIsPublished(false);
    setPublishSaving(false);
  };

  const shirtEntries = Object.entries(p.shirts || {}).filter(([,v]) => v > 0);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #2D5016, #7BA05B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#fff', fontFamily: "'Nunito', sans-serif", fontSize: '1.1rem', opacity: 0.8 }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F4F8EE', fontFamily: "'Nunito', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #2D5016, #7BA05B)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              ← Dashboard
            </button>
          )}
          <img src="/Camp_Phillip_Logo.png" alt="Camp Phillip" style={{ height: 40, filter: 'brightness(0) invert(1)' }} />
        </div>
        {!isAdminView && (
          <UserMenu
            email={displayEmail}
            photoUrl={dirProfile.photoUrl}
            onMyProfile={null}
            onGoToForm={onGoToForm}
            onSignOut={onSignOut}
            isOnProfile={true}
          />
        )}
        {isAdminView && (
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', fontWeight: 600 }}>Admin view</span>
        )}
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 20px 60px' }}>

        {/* ── Admin publish banner ── */}
        {isAdminView && (
          <div style={{ background: isPublished ? '#EAF4E2' : '#FEF6E4', border: `1px solid ${isPublished ? '#7BA05B' : '#FAC775'}`, borderRadius: 12, padding: '14px 18px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span style={{ fontWeight: 800, fontSize: '0.88rem', color: isPublished ? '#2D5016' : '#7a4e00' }}>
                {isPublished ? '🌐 Live on campphillip.com' : '⏳ Not yet published'}
              </span>
              <div style={{ fontSize: '0.78rem', color: '#9B9B9B', marginTop: 3 }}>
                {!isPublished && dirProfile.submittedForDirectory && 'Staff submitted — ready to publish.'}
                {!isPublished && !dirProfile.submittedForDirectory && 'You can fill in and publish without staff input.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!isPublished && (
                <button onClick={publishToDirectory} disabled={publishSaving || !dirProfile.positionTitle}
                  style={{ ...s.saveBtn, background: dirProfile.positionTitle ? '#2E7DAF' : '#C8D9B0', cursor: dirProfile.positionTitle ? 'pointer' : 'not-allowed' }}>
                  {publishSaving ? 'Publishing…' : '🌐 Publish to website'}
                </button>
              )}
              {isPublished && (
                <button onClick={removeFromDirectory} disabled={publishSaving}
                  style={{ ...s.cancelBtn, color: '#a32d2d', borderColor: '#f7c1c1' }}>
                  {publishSaving ? 'Removing…' : 'Remove from website'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Profile hero ── */}
        <div style={{ background: '#fff', border: '1px solid #D4E8B0', borderRadius: 16, padding: '24px 24px 20px', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <input
                id="dp-photo-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => handleFileSelect(e.target.files[0])}
              />
              <label
                htmlFor={dpEditing ? 'dp-photo-upload' : undefined}
                style={{ display: 'flex', width: 90, height: 90, borderRadius: '50%', overflow: 'hidden', border: '3px solid #D4E8B0', cursor: dpEditing ? 'pointer' : 'default', position: 'relative', background: '#2D5016', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.8rem', fontWeight: 800, margin: 0 }}
              >
                {dirProfile.photoUrl
                  ? <img src={dirProfile.photoUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : initials}
                {dpEditing && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.48)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, pointerEvents: 'none' }}>
                    <span style={{ fontSize: '1rem' }}>📷</span>
                    <span style={{ color: '#fff', fontSize: '0.58rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.3 }}>
                      {dirProfile.photoUrl ? 'Change Photo' : 'Add Photo'}
                    </span>
                  </div>
                )}
              </label>
              {photoUploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#2D5016', fontWeight: 700 }}>…</div>
              )}
            </div>

            {/* Name + role + chips */}
            <div style={{ flex: 1, minWidth: 200 }}>
              {!dpEditing ? (
                <>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1a2e0a', lineHeight: 1.2, marginBottom: 4 }}>{displayName}</div>
                  {dirProfile.positionTitle && (
                    <div style={{ fontSize: '0.95rem', color: '#5A7A3A', fontWeight: 600, marginBottom: 10 }}>{dirProfile.positionTitle}</div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: dirProfile.bio ? 12 : 0 }}>
                    {dirProfile.hometown && <span style={s.chip}>📍 {dirProfile.hometown}</span>}
                    {formatStints(dirProfile.saltStints)
                      ? <span style={s.chip}>⭐ {formatStints(dirProfile.saltStints)}</span>
                      : dirProfile.saltYears
                        ? <span style={s.chip}>⭐ Year {dirProfile.saltYears} on SALT</span>
                        : null}
                  </div>
                  {dirProfile.bio && (
                    <p style={{ fontSize: '0.88rem', color: '#4a5a3a', lineHeight: 1.65, margin: 0 }}>{dirProfile.bio}</p>
                  )}
                  {!dirProfile.positionTitle && !dirProfile.bio && (
                    <p style={{ fontSize: '0.85rem', color: '#aaa', fontStyle: 'italic', margin: 0 }}>Add a position, bio, and photo to complete your profile.</p>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                  <Field label="Position / title">
                    <TextInput value={dpDraft.positionTitle} onChange={setDp('positionTitle')} placeholder="e.g. Head Counselor" />
                  </Field>
                  <Field label="Hometown">
                    <TextInput value={dpDraft.hometown} onChange={setDp('hometown')} placeholder="e.g. Madison, WI" />
                  </Field>
                  <Field label="Years on SALT">
                    <StintsEditor
                      stints={dpDraft.saltStints || []}
                      onChange={val => setDpDraft(prev => ({ ...prev, saltStints: val }))}
                    />
                  </Field>
                  <Field label="Bio (2–3 sentences for campers)">
                    <textarea
                      value={dpDraft.bio || ''}
                      onChange={e => setDp('bio')(e.target.value)}
                      placeholder="Tell campers a little about yourself..."
                      style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' }}
                    />
                  </Field>
                </div>
              )}
            </div>

            {/* Edit button */}
            {submission && !dpEditing && (
              <button onClick={() => { setDpEditing(true); setDpDraft({ ...dirProfile, saltStints: dirProfile.saltStints?.length ? dirProfile.saltStints : [{ from: THIS_YEAR, to: 'present' }] }); }} style={s.editBtn}>
                Edit profile
              </button>
            )}
            {dpEditing && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flexShrink: 0 }}>
                <button onClick={() => { setDpEditing(false); setDpDraft({}); }} style={s.cancelBtn}>Cancel</button>
                <button onClick={() => saveDirProfile(dpDraft)} disabled={dpSaving} style={s.saveBtn}>
                  {dpSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {/* Staff submit-for-review */}
          {!isAdminView && submission && !dirProfile.submittedForDirectory && (
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #F0F7E8' }}>
              <button
                onClick={submitForReview}
                disabled={dpSaving || !dirProfile.positionTitle || !dirProfile.bio}
                style={{ width: '100%', background: dirProfile.positionTitle && dirProfile.bio ? '#2E7DAF' : '#C8D9B0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: '0.88rem', cursor: dirProfile.positionTitle && dirProfile.bio ? 'pointer' : 'not-allowed' }}>
                Submit profile for directory review
              </button>
            </div>
          )}
          {!isAdminView && dirProfile.submittedForDirectory && (
            <div style={{ marginTop: 16, padding: '10px 14px', background: '#EAF4E2', borderTop: '1px solid #a8d88a', fontSize: '0.85rem', fontWeight: 700, color: '#2D5016', textAlign: 'center', marginLeft: -24, marginRight: -24, marginBottom: -20, borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              ✅ Submitted — your profile is pending admin approval
            </div>
          )}
        </div>

        {/* ── Season card (collapsible) ── */}
        {submission && (
          <div style={{ background: '#fff', border: '1px solid #D4E8B0', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
            <button
              onClick={() => setSeasonOpen(v => !v)}
              style={{ width: '100%', background: 'none', border: 'none', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#2D5016' }}>📋 Summer {CURRENT_YEAR}</span>
                <span style={{ background: sc.bg, color: sc.color, fontSize: '0.75rem', fontWeight: 700, padding: '2px 10px', borderRadius: 20, border: `1px solid ${sc.color}33` }}>{sc.label}</span>
              </div>
              <span style={{ color: '#5A7A3A', fontSize: '0.85rem', transform: seasonOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▾</span>
            </button>

            {seasonOpen && (
              <div style={{ padding: '0 20px 18px', borderTop: '1px solid #F0F7E8' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
                  {/* Forms */}
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7F5A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Forms</div>
                    <FormCheck label="W-4"           done={!!f.w4} />
                    <FormCheck label="I-9"           done={!!f.i9} />
                    <FormCheck label="Medical Form"  done={!!f.medical} />
                    <FormCheck label="CPR / First Aid" done={!!f.cpr} />
                    <FormCheck label="Direct Deposit" done={!!f.directdeposit} />
                    <FormCheck label="Background Check" done={!!f.bgCheckDone} />
                  </div>

                  {/* Availability */}
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7F5A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Availability</div>
                    <InfoRow label="Ropes Training" value={a.ropes === true ? `Yes — ${a.ropesLevel || ''}`.trim() : a.ropes === false ? 'No' : null} />
                    <InfoRow label="Fancy Schmancy"  value={a.fancy === true ? `Yes — ${(a.fancyRoles||[]).join(', ')||''}` : a.fancy === false ? 'No' : null} />
                    <InfoRow label="Junior Staff"    value={a.junior || null} />
                  </div>

                  {/* Shirts */}
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7F5A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>T-Shirts</div>
                    {shirtEntries.length > 0
                      ? shirtEntries.map(([sz, qty]) => (
                          <div key={sz} style={{ fontSize: '0.85rem', color: '#2D2D2D', marginBottom: 4 }}>
                            <strong>{sz}</strong> × {qty}
                          </div>
                        ))
                      : <span style={{ fontSize: '0.85rem', color: '#9B9B9B' }}>None ordered yet</span>}
                  </div>
                </div>

                {!isAdminView && (
                  <button onClick={onGoToForm} style={{ marginTop: 18, padding: '9px 20px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' }}>
                    {status === 'complete' ? 'Review my form →' : 'Continue onboarding →'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Personal info sections ── */}
        {submission && (
          <>
            {/* Contact + Emergency in a 2-col grid on wider screens */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 0 }}>

              <SectionCard
                title="Contact Info"
                editing={editSection === 'contact'}
                onEdit={() => startEdit('contact', { firstName: p.firstName, lastName: p.lastName, phone: p.phone, dob: p.dob, address: p.address, city: p.city, state: p.state, zip: p.zip })}
                onSave={() => saveEdit('contact', draft)}
                onCancel={cancelEdit}
                saving={saving}
              >
                {editSection === 'contact' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                    <Field label="First name"><TextInput value={draft.firstName} onChange={set('firstName')} /></Field>
                    <Field label="Last name"><TextInput value={draft.lastName} onChange={set('lastName')} /></Field>
                    <Field label="Phone"><TextInput value={draft.phone} onChange={v => set('phone')(formatPhone(v))} type="tel" /></Field>
                    <Field label="Date of birth"><TextInput value={draft.dob} onChange={set('dob')} /></Field>
                    <div style={{ gridColumn: '1/-1' }}>
                      <Field label="Address"><TextInput value={draft.address} onChange={set('address')} /></Field>
                    </div>
                    <Field label="City"><TextInput value={draft.city} onChange={set('city')} /></Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
                      <Field label="State"><TextInput value={draft.state} onChange={set('state')} /></Field>
                      <Field label="ZIP"><TextInput value={draft.zip} onChange={set('zip')} /></Field>
                    </div>
                  </div>
                ) : (
                  <>
                    <InfoRow label="Name"    value={p.firstName ? `${p.firstName} ${p.lastName || ''}`.trim() : null} />
                    <InfoRow label="Phone"   value={formatPhone(p.phone)} />
                    <InfoRow label="DOB"     value={p.dob} />
                    <InfoRow label="Address" value={[p.address, p.city, p.state, p.zip].filter(Boolean).join(', ')} />
                  </>
                )}
              </SectionCard>

              <SectionCard
                title="Emergency Contact"
                editing={editSection === 'emergency'}
                onEdit={() => startEdit('emergency', { emergencyName: p.emergencyName, emergencyRelationship: p.emergencyRelationship, emergencyPhone: p.emergencyPhone })}
                onSave={() => saveEdit('emergency', draft)}
                onCancel={cancelEdit}
                saving={saving}
              >
                {editSection === 'emergency' ? (
                  <>
                    <Field label="Name"><TextInput value={draft.emergencyName} onChange={set('emergencyName')} /></Field>
                    <Field label="Relationship"><TextInput value={draft.emergencyRelationship} onChange={set('emergencyRelationship')} /></Field>
                    <Field label="Phone"><TextInput value={draft.emergencyPhone} onChange={v => set('emergencyPhone')(formatPhone(v))} type="tel" /></Field>
                  </>
                ) : (
                  <>
                    <InfoRow label="Name"         value={p.emergencyName} />
                    <InfoRow label="Relationship" value={p.emergencyRelationship} />
                    <InfoRow label="Phone"        value={formatPhone(p.emergencyPhone)} />
                  </>
                )}
              </SectionCard>
            </div>

            <div style={{ marginTop: 14 }}>
              <SectionCard
                title="Medical Info"
                editing={editSection === 'medical'}
                onEdit={() => startEdit('medical', { allergies: p.allergies, medications: p.medications, medicalNotes: p.medicalNotes })}
                onSave={() => saveEdit('medical', draft)}
                onCancel={cancelEdit}
                saving={saving}
              >
                {editSection === 'medical' ? (
                  <>
                    <Field label="Allergies"><TextInput value={draft.allergies} onChange={set('allergies')} /></Field>
                    <Field label="Medications"><TextInput value={draft.medications} onChange={set('medications')} /></Field>
                    <Field label="Notes">
                      <textarea value={draft.medicalNotes || ''} onChange={e => set('medicalNotes')(e.target.value)}
                        style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', resize: 'vertical', minHeight: 70, boxSizing: 'border-box' }} />
                    </Field>
                  </>
                ) : (
                  <>
                    <InfoRow label="Allergies"   value={p.allergies} />
                    <InfoRow label="Medications" value={p.medications} />
                    <InfoRow label="Notes"       value={p.medicalNotes} />
                  </>
                )}
              </SectionCard>
            </div>
          </>
        )}

        {!submission && !loading && (
          <div style={{ background: '#fff', border: '1px solid #D4E8B0', borderRadius: 12, padding: '32px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>📋</div>
            <p style={{ color: '#5A7A3A', fontWeight: 600, marginBottom: 16 }}>No onboarding data yet for this account.</p>
            {onGoToForm && (
              <button onClick={onGoToForm} style={{ padding: '10px 24px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
                Start onboarding →
              </button>
            )}
          </div>
        )}

      </div>

      {/* ── Photo crop modal ───────────────────────────────────── */}
      {cropSrc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', width: '100%', maxWidth: 360, boxShadow: '0 24px 64px rgba(0,0,0,0.4)', fontFamily: "'Nunito', sans-serif" }}>

            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #D4E8B0' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1a2e0a' }}>Crop your photo</div>
              <div style={{ fontSize: '0.75rem', color: '#6B7F5A', marginTop: 2 }}>Drag to reposition · Use the slider to zoom</div>
            </div>

            {/* Crop frame */}
            <div
              style={{ position: 'relative', width: CROP_FRAME, height: CROP_FRAME, margin: '0 auto', overflow: 'hidden', cursor: 'grab', userSelect: 'none', background: '#111', touchAction: 'none' }}
              onMouseDown={e => {
                e.preventDefault();
                const startX = e.clientX - cropOffset.x, startY = e.clientY - cropOffset.y;
                const iw = cropNatural.w * cropScale, ih = cropNatural.h * cropScale;
                const clamp = (nx, ny) => ({
                  x: Math.min(0, Math.max(CROP_FRAME - iw, nx)),
                  y: Math.min(0, Math.max(CROP_FRAME - ih, ny)),
                });
                const onMove = me => setCropOffset(clamp(me.clientX - startX, me.clientY - startY));
                const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
              onTouchStart={e => {
                const t = e.touches[0];
                const startX = t.clientX - cropOffset.x, startY = t.clientY - cropOffset.y;
                const iw = cropNatural.w * cropScale, ih = cropNatural.h * cropScale;
                const clamp = (nx, ny) => ({
                  x: Math.min(0, Math.max(CROP_FRAME - iw, nx)),
                  y: Math.min(0, Math.max(CROP_FRAME - ih, ny)),
                });
                const onMove = te => { const tt = te.touches[0]; setCropOffset(clamp(tt.clientX - startX, tt.clientY - startY)); };
                const onEnd  = () => { window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
                window.addEventListener('touchmove', onMove, { passive: false });
                window.addEventListener('touchend', onEnd);
              }}
            >
              {/* Image */}
              <img
                ref={cropImgRef}
                src={cropSrc}
                alt="crop preview"
                draggable={false}
                style={{ position: 'absolute', left: cropOffset.x, top: cropOffset.y, width: cropNatural.w * cropScale, height: cropNatural.h * cropScale, pointerEvents: 'none', userSelect: 'none', display: 'block' }}
              />
              {/* Dark vignette with circular window + dashed guide ring */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <defs>
                  <mask id="cropHole">
                    <rect width="100%" height="100%" fill="white" />
                    <circle cx={CROP_FRAME / 2} cy={CROP_FRAME / 2} r={CROP_FRAME / 2 - 2} fill="black" />
                  </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#cropHole)" />
                <circle cx={CROP_FRAME / 2} cy={CROP_FRAME / 2} r={CROP_FRAME / 2 - 2} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeDasharray="6 4" />
              </svg>
            </div>

            {/* Zoom slider */}
            <div style={{ padding: '14px 20px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7F5A', flexShrink: 0 }}>Zoom</span>
              <input
                type="range"
                min={cropMinScale}
                max={cropMinScale * 4}
                step={cropMinScale * 0.01}
                value={cropScale}
                onChange={e => setCropScale(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: '#2D5016' }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, padding: '12px 20px 20px' }}>
              <button
                onClick={() => setCropSrc(null)}
                style={{ flex: 1, padding: '10px', background: 'transparent', border: '1.5px solid #C8D9B0', borderRadius: 9, color: '#2D5016', fontWeight: 700, fontFamily: "'Nunito', sans-serif", cursor: 'pointer', fontSize: '0.9rem' }}>
                Cancel
              </button>
              <button
                onClick={confirmCrop}
                style={{ flex: 2, padding: '10px', background: '#2D5016', border: 'none', borderRadius: 9, color: '#fff', fontWeight: 700, fontFamily: "'Nunito', sans-serif", cursor: 'pointer', fontSize: '0.9rem' }}>
                Use photo
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const s = {
  editBtn: {
    padding: '5px 14px', background: '#F4F8EE', border: '1.5px solid #C8D9B0', borderRadius: 8,
    fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', color: '#2D5016', cursor: 'pointer', flexShrink: 0,
  },
  saveBtn: {
    padding: '6px 16px', background: '#2D5016', border: 'none', borderRadius: 8,
    fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', color: '#fff', cursor: 'pointer',
  },
  cancelBtn: {
    padding: '6px 14px', background: 'transparent', border: '1.5px solid #C8D9B0', borderRadius: 8,
    fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', color: '#6B7F5A', cursor: 'pointer',
  },
  chip: {
    background: '#EAF4E2', color: '#2D5016', border: '1px solid #C0DD97',
    borderRadius: 20, padding: '3px 11px', fontSize: '0.78rem', fontWeight: 700,
  },
};
